import { Minus, Plus } from 'lucide-react';
import { Eyebrow, IconButton } from '@/components/ui';
import { useTaskStore } from '@/stores/taskStore';
import type { Task } from '@/types';

const STEP = 15;
const MIN = 5;

export function DurationField({ task }: { task: Task }) {
  const updateTask = useTaskStore((s) => s.updateTask);

  const change = (delta: number) => {
    const next = Math.max(MIN, task.durationMinutes + delta);
    const patch: { durationMinutes: number; scheduledEnd?: Date } = { durationMinutes: next };
    // Keep a scheduled task's end in sync with its duration so the NOW card's
    // "X min left" and the timeline block length update immediately.
    if (task.scheduledStart) {
      patch.scheduledEnd = new Date(task.scheduledStart.getTime() + next * 60_000);
    }
    updateTask(task.id, patch);
  };

  return (
    <div>
      <Eyebrow className="mb-1">Duration</Eyebrow>
      <div className="flex items-center gap-2">
        <IconButton
          label="Decrease duration"
          size="sm"
          icon={<Minus className="h-3 w-3" />}
          onClick={() => change(-STEP)}
        />
        {/* aria-live so the +/− buttons' effect is announced without moving focus. */}
        <span
          aria-live="polite"
          className="min-w-[60px] font-mono text-body-lg text-t1 tabular-nums"
        >
          {task.durationMinutes} min
        </span>
        <IconButton
          label="Increase duration"
          size="sm"
          icon={<Plus className="h-3 w-3" />}
          onClick={() => change(STEP)}
        />
      </div>
    </div>
  );
}
